import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaWhatsapp, FaInstagram, FaFacebook, FaGoogle, FaCalendarAlt, FaImage, FaVideo, FaBullhorn, FaTh } from 'react-icons/fa';
import { API_URL } from '../config/api';
import './AutomationManager.css';

const AutomationManager = () => {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    platform: 'whatsapp',
    type: 'post', // post, reel, image, ad
    content: '',
    image: '',
    video: '',
    scheduledDate: '',
    scheduledTime: '',
    targetAudience: '',
    budget: '',
    dailyBudget: '', // for ads - daily budget option
    duration: '', // for ads
    status: 'pending',
    // Ad-specific fields
    adAccountId: '', // Required for Meta ads (act_xxxxx)
    campaignName: '', // Campaign name
    adSetName: '', // Ad Set name
    adName: '', // Ad name
    objective: 'OUTCOME_TRAFFIC', // Campaign objective
    callToActionType: 'LEARN_MORE', // CTA button type
    link: '', // URL for ads
    ageMin: 18, // Targeting: minimum age
    ageMax: 65, // Targeting: maximum age
    gender: 'all', // Targeting: gender
    countries: 'US' // Targeting: countries (comma-separated)
  });
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchScheduledPosts();
  }, [activeTab]);

  const fetchScheduledPosts = async () => {
    setLoading(true);
    try {
      // For Meta tab, fetch both Facebook and Instagram posts
      if (activeTab === 'meta') {
        const [fbResponse, igResponse] = await Promise.all([
          axios.get(`${API_URL}/api/automation?platform=facebook`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { data: [] } })),
          axios.get(`${API_URL}/api/automation?platform=instagram`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { data: [] } }))
        ]);
        setScheduledPosts([
          ...(fbResponse.data.data || []),
          ...(igResponse.data.data || [])
        ]);
      } else {
        const response = await axios.get(`${API_URL}/api/automation?platform=${activeTab}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setScheduledPosts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
      setScheduledPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value, platform: activeTab });
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('uploadType', 'automation');

    try {
      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const imageUrl = response.data.data.baseS3Url || response.data.data.url;
      if (type === 'image') {
        setFormData({ ...formData, image: imageUrl });
      } else if (type === 'video') {
        setFormData({ ...formData, video: imageUrl });
      }
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.response?.data?.message || 'File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content && !formData.image && !formData.video) {
      toast.error('Please provide content, image, or video');
      return;
    }

    // Validation for ads
    if (formData.type === 'ad') {
      if (activeTab === 'meta' && !formData.adAccountId) {
        toast.error('Please provide Ad Account ID (act_xxxxx) for Meta ads');
        return;
      }
      if (activeTab === 'meta' && !formData.metaPlatform) {
        toast.error('Please select a platform (Facebook, Instagram, or Both)');
        return;
      }
      if (!formData.campaignName || !formData.adSetName || !formData.adName) {
        toast.error('Please provide Campaign Name, Ad Set Name, and Ad Name');
        return;
      }
      if (!formData.dailyBudget && !formData.budget) {
        toast.error('Please provide either Daily Budget or Lifetime Budget');
        return;
      }
      if (!formData.link) {
        toast.error('Please provide a Link URL for the ad');
        return;
      }
    }

    try {
      // For ads, send to Meta Marketing API endpoints
      if (formData.type === 'ad' && activeTab === 'meta') {
        // Build targeting object for Meta ads
        const targeting = {
          age_min: parseInt(formData.ageMin) || 18,
          age_max: parseInt(formData.ageMax) || 65,
          genders: formData.gender === 'all' ? [1, 2] : formData.gender === 'male' ? [2] : [1],
          geo_locations: {
            countries: formData.countries.split(',').map(c => c.trim())
          }
        };

        const adData = {
          campaignName: formData.campaignName,
          adSetName: formData.adSetName,
          adName: formData.adName,
          message: formData.content,
          imageUrl: formData.image,
          link: formData.link,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          dailyBudget: formData.dailyBudget ? parseFloat(formData.dailyBudget) : null,
          targetAudience: targeting,
          objective: formData.objective,
          callToActionType: formData.callToActionType,
          adAccountId: formData.adAccountId,
          status: 'PAUSED' // Start as PAUSED for review
        };

        // Call appropriate Meta ads endpoint (automation service)
        const automationServiceUrl = process.env.REACT_APP_AUTOMATION_SERVICE_URL || 'http://localhost:5001';
        const endpoint = activeTab === 'facebook' 
          ? `${automationServiceUrl}/api/meta/facebook/ad`
          : `${automationServiceUrl}/api/meta/instagram/ad`;

        const response = await axios.post(endpoint, adData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success('Ad campaign created successfully! It will be created in PAUSED status for review.');
        // Reset form
        setFormData({
          platform: activeTab,
          type: 'post',
          content: '',
          image: '',
          video: '',
          scheduledDate: '',
          scheduledTime: '',
          targetAudience: '',
          budget: '',
          dailyBudget: '',
          duration: '',
          status: 'pending',
          adAccountId: '',
          campaignName: '',
          adSetName: '',
          adName: '',
          objective: 'OUTCOME_TRAFFIC',
          callToActionType: 'LEARN_MORE',
          link: '',
          ageMin: 18,
          ageMax: 65,
          gender: 'all',
          countries: 'US'
        });
        fetchScheduledPosts();
        return;
      }

      // For regular posts/reels, use existing automation endpoint
      // For Meta tab, handle platform selection
      if (activeTab === 'meta') {
        const selectedPlatform = formData.metaPlatform || 'both';
        
        // If both platforms selected, create posts for both
        if (selectedPlatform === 'both') {
          const platformsToSubmit = ['facebook', 'instagram'];
          const results = [];
          
          for (const platform of platformsToSubmit) {
            try {
              const submitData = {
                ...formData,
                platform: platform,
                scheduledDateTime: formData.scheduledDate && formData.scheduledTime 
                  ? `${formData.scheduledDate}T${formData.scheduledTime}:00`
                  : null
              };
              
              const response = await axios.post(`${API_URL}/api/automation`, submitData, {
                headers: { Authorization: `Bearer ${token}` },
              });
              results.push({ platform, success: true });
            } catch (error) {
              results.push({ platform, success: false, error: error.response?.data?.message || error.message });
            }
          }
          
          const successCount = results.filter(r => r.success).length;
          if (successCount === 2) {
            toast.success('Post scheduled successfully for both Facebook and Instagram!');
          } else if (successCount === 1) {
            toast.warning(`Post scheduled for ${results.find(r => r.success)?.platform} only. One platform failed.`);
          } else {
            toast.error('Failed to schedule post on both platforms');
          }
          
          // Reset form
          setFormData({
            platform: activeTab,
            type: 'post',
            content: '',
            image: '',
            video: '',
            scheduledDate: '',
            scheduledTime: '',
            targetAudience: '',
            budget: '',
            dailyBudget: '',
            duration: '',
            status: 'pending',
            adAccountId: '',
            campaignName: '',
            adSetName: '',
            adName: '',
            objective: 'OUTCOME_TRAFFIC',
            callToActionType: 'LEARN_MORE',
            link: '',
            ageMin: 18,
            ageMax: 65,
            gender: 'all',
            countries: 'US',
            metaPlatform: 'both'
          });
          fetchScheduledPosts();
          return;
        } else {
          // Single platform selected
          const submitData = {
            ...formData,
            platform: selectedPlatform,
            scheduledDateTime: formData.scheduledDate && formData.scheduledTime 
              ? `${formData.scheduledDate}T${formData.scheduledTime}:00`
              : null
          };
          
          const response = await axios.post(`${API_URL}/api/automation`, submitData, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          toast.success(`Post scheduled successfully for ${selectedPlatform === 'facebook' ? 'Facebook' : 'Instagram'}!`);
          // Reset form (same as above)
          setFormData({
            platform: activeTab,
            type: 'post',
            content: '',
            image: '',
            video: '',
            scheduledDate: '',
            scheduledTime: '',
            targetAudience: '',
            budget: '',
            dailyBudget: '',
            duration: '',
            status: 'pending',
            adAccountId: '',
            campaignName: '',
            adSetName: '',
            adName: '',
            objective: 'OUTCOME_TRAFFIC',
            callToActionType: 'LEARN_MORE',
            link: '',
            ageMin: 18,
            ageMax: 65,
            gender: 'all',
            countries: 'US',
            metaPlatform: 'both'
          });
          fetchScheduledPosts();
          return;
        }
      }
      
      // For other platforms (WhatsApp, Google)
      const submitData = {
        ...formData,
        platform: activeTab,
        scheduledDateTime: formData.scheduledDate && formData.scheduledTime 
          ? `${formData.scheduledDate}T${formData.scheduledTime}:00`
          : null
      };

      const response = await axios.post(`${API_URL}/api/automation`, submitData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Automation scheduled successfully!');
      setFormData({
        platform: activeTab,
        type: 'post',
        content: '',
        image: '',
        video: '',
        scheduledDate: '',
        scheduledTime: '',
        targetAudience: '',
        budget: '',
        dailyBudget: '',
        duration: '',
        status: 'pending',
        adAccountId: '',
        campaignName: '',
        adSetName: '',
        adName: '',
        objective: 'OUTCOME_TRAFFIC',
        callToActionType: 'LEARN_MORE',
        link: '',
        ageMin: 18,
        ageMax: 65,
        gender: 'all',
        countries: 'US'
      });
      fetchScheduledPosts();
    } catch (error) {
      console.error('Error scheduling automation:', error);
      toast.error(error.response?.data?.message || 'Failed to schedule automation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scheduled post?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/automation/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Scheduled post deleted successfully!');
      fetchScheduledPosts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete scheduled post');
    }
  };

  const platforms = [
    { id: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
    { id: 'meta', name: 'Meta (Facebook & Instagram)', icon: FaTh, color: '#0084FF' },
    { id: 'google', name: 'Google Ads', icon: FaGoogle, color: '#4285F4' },
  ];

  const postTypes = [
    { value: 'post', label: 'Post', icon: FaImage },
    { value: 'reel', label: 'Reel', icon: FaVideo },
    { value: 'image', label: 'Image', icon: FaImage },
    { value: 'ad', label: 'Ad Campaign', icon: FaBullhorn },
  ];

  const ActiveIcon = platforms.find(p => p.id === activeTab)?.icon || FaWhatsapp;

  return (
    <div className="automation-manager">
      <div className="header">
        <h2>🤖 AI Automation Manager</h2>
        <p className="subtitle">Automate posts, reels, and ads across multiple platforms</p>
      </div>

      {/* Platform Tabs */}
      <div className="platform-tabs">
        {platforms.map((platform) => {
          const Icon = platform.icon;
          return (
            <button
              key={platform.id}
              className={`platform-tab ${activeTab === platform.id ? 'active' : ''}`}
              onClick={() => setActiveTab(platform.id)}
              style={{ '--platform-color': platform.color }}
            >
              <Icon />
              <span>{platform.name}</span>
            </button>
          );
        })}
      </div>

      <div className="automation-content">
        {/* Form Section */}
        <div className="form-section">
          <h3>
            <ActiveIcon style={{ marginRight: '10px', color: platforms.find(p => p.id === activeTab)?.color }} />
            Create New {platforms.find(p => p.id === activeTab)?.name} Automation
            {activeTab === 'meta' && (
              <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                (Facebook & Instagram)
              </span>
            )}
          </h3>

          <form onSubmit={handleSubmit} className="automation-form">
            {/* Post Type Selection */}
            <div className="form-group">
              <label>Content Type *</label>
              <div className="post-type-buttons">
                {postTypes.map((type) => {
                  const TypeIcon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      className={`post-type-btn ${formData.type === type.value ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, type: type.value })}
                    >
                      <TypeIcon />
                      <span>{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="form-group">
              <label>Content / Caption *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows="5"
                placeholder="Enter your post content, caption, or ad copy..."
                required
              />
            </div>

            {/* Image Upload - Show for posts, reels, images, and ads (for Meta) */}
            {(formData.type === 'post' || formData.type === 'image' || formData.type === 'reel' || (formData.type === 'ad' && activeTab === 'meta')) && (
              <div className="form-group">
                <label>
                  {formData.type === 'reel' ? 'Video' : 'Image'} Upload
                  {formData.type === 'reel' && ' (for Reels)'}
                </label>
                <input
                  type="file"
                  accept={formData.type === 'reel' ? 'video/*' : 'image/*'}
                  onChange={(e) => handleFileChange(e, formData.type === 'reel' ? 'video' : 'image')}
                  disabled={uploading}
                />
                {uploading && <p className="upload-status">Uploading...</p>}
                {formData.image && formData.type !== 'reel' && (
                  <div className="image-preview">
                    <img src={formData.image} alt="Preview" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: '' })}
                      className="remove-image-btn"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {formData.video && formData.type === 'reel' && (
                  <div className="image-preview">
                    <video src={formData.video} controls style={{ maxWidth: '300px' }} />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, video: '' })}
                      className="remove-image-btn"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Scheduling */}
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FaCalendarAlt style={{ marginRight: '5px' }} />
                  Schedule Date
                </label>
                <input
                  type="date"
                  name="scheduledDate"
                  value={formData.scheduledDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Schedule Time</label>
                <input
                  type="time"
                  name="scheduledTime"
                  value={formData.scheduledTime}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Meta Platform Selection (for Meta tab - all content types) */}
            {activeTab === 'meta' && (
              <div className="form-group">
                <label>Select Platform(s) *</label>
                <div className="post-type-buttons">
                  <button
                    type="button"
                    className={`post-type-btn ${formData.metaPlatform === 'facebook' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, metaPlatform: 'facebook' })}
                  >
                    <FaFacebook style={{ marginRight: '5px' }} />
                    Facebook Only
                  </button>
                  <button
                    type="button"
                    className={`post-type-btn ${formData.metaPlatform === 'instagram' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, metaPlatform: 'instagram' })}
                  >
                    <FaInstagram style={{ marginRight: '5px' }} />
                    Instagram Only
                  </button>
                  <button
                    type="button"
                    className={`post-type-btn ${formData.metaPlatform === 'both' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, metaPlatform: 'both' })}
                  >
                    <FaTh style={{ marginRight: '5px' }} />
                    Both Platforms
                  </button>
                </div>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  {formData.type === 'ad' 
                    ? 'For ads, you can create campaigns for one or both platforms'
                    : 'For posts/reels, select which platform(s) to publish to'}
                </small>
              </div>
            )}

            {/* Ad Specific Fields */}
            {formData.type === 'ad' && (
              <>
                {/* Ad Account ID - Required for Meta Ads */}
                {activeTab === 'meta' && (
                  <div className="form-group">
                    <label>Ad Account ID * <span style={{ fontSize: '12px', color: '#666' }}>(act_xxxxx)</span></label>
                    <input
                      type="text"
                      name="adAccountId"
                      value={formData.adAccountId}
                      onChange={handleInputChange}
                      placeholder="act_123456789"
                      required={formData.type === 'ad' && activeTab === 'meta'}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Find this in Facebook Ads Manager → Account Settings → Ad Account ID
                    </small>
                  </div>
                )}

                {/* Campaign Names */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      name="campaignName"
                      value={formData.campaignName}
                      onChange={handleInputChange}
                      placeholder="e.g., Summer Sale Campaign"
                      required={formData.type === 'ad'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Ad Set Name *</label>
                    <input
                      type="text"
                      name="adSetName"
                      value={formData.adSetName}
                      onChange={handleInputChange}
                      placeholder="e.g., Product Promotion Ad Set"
                      required={formData.type === 'ad'}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Ad Name *</label>
                  <input
                    type="text"
                    name="adName"
                    value={formData.adName}
                    onChange={handleInputChange}
                    placeholder="e.g., Product Showcase Ad"
                    required={formData.type === 'ad'}
                  />
                </div>

                {/* Link for Ads */}
                <div className="form-group">
                  <label>Link URL *</label>
                  <input
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleInputChange}
                    placeholder="https://yourwebsite.com/product"
                    required={formData.type === 'ad'}
                  />
                </div>

                {/* Budget Options */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Daily Budget ($) *</label>
                    <input
                      type="number"
                      name="dailyBudget"
                      value={formData.dailyBudget}
                      onChange={handleInputChange}
                      min="1"
                      step="0.01"
                      placeholder="e.g., 10.00"
                      required={formData.type === 'ad'}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>Minimum $1 per day</small>
                  </div>
                  <div className="form-group">
                    <label>Lifetime Budget ($) <span style={{ fontSize: '12px', color: '#666' }}>(Optional)</span></label>
                    <input
                      type="number"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="Total campaign budget"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>Leave empty to use daily budget only</small>
                  </div>
                </div>

                {/* Campaign Objective */}
                <div className="form-group">
                  <label>Campaign Objective *</label>
                  <select
                    name="objective"
                    value={formData.objective}
                    onChange={handleInputChange}
                    required={formData.type === 'ad'}
                  >
                    <option value="OUTCOME_TRAFFIC">Traffic (Drive website visits)</option>
                    <option value="OUTCOME_ENGAGEMENT">Engagement (Get more post engagement)</option>
                    <option value="OUTCOME_LEADS">Leads (Collect leads)</option>
                    <option value="OUTCOME_AWARENESS">Awareness (Increase brand awareness)</option>
                    <option value="OUTCOME_SALES">Sales (Drive sales)</option>
                    <option value="OUTCOME_APP_PROMOTION">App Promotion (Get app installs)</option>
                  </select>
                </div>

                {/* Call to Action */}
                <div className="form-group">
                  <label>Call to Action Button *</label>
                  <select
                    name="callToActionType"
                    value={formData.callToActionType}
                    onChange={handleInputChange}
                    required={formData.type === 'ad'}
                  >
                    <option value="LEARN_MORE">Learn More</option>
                    <option value="SHOP_NOW">Shop Now</option>
                    <option value="SIGN_UP">Sign Up</option>
                    <option value="DOWNLOAD">Download</option>
                    <option value="BOOK_TRAVEL">Book Travel</option>
                    <option value="CONTACT_US">Contact Us</option>
                    <option value="GET_QUOTE">Get Quote</option>
                  </select>
                </div>

                {/* Targeting Options */}
                <div className="form-group">
                  <label style={{ marginBottom: '10px', display: 'block' }}>Targeting Options</label>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Min Age</label>
                      <input
                        type="number"
                        name="ageMin"
                        value={formData.ageMin}
                        onChange={handleInputChange}
                        min="13"
                        max="65"
                      />
                    </div>
                    <div className="form-group">
                      <label>Max Age</label>
                      <input
                        type="number"
                        name="ageMax"
                        value={formData.ageMax}
                        onChange={handleInputChange}
                        min="13"
                        max="65"
                      />
                    </div>
                    <div className="form-group">
                      <label>Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                      >
                        <option value="all">All</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: '10px' }}>
                    <label>Countries (comma-separated) *</label>
                    <input
                      type="text"
                      name="countries"
                      value={formData.countries}
                      onChange={handleInputChange}
                      placeholder="US, IN, GB"
                      required={formData.type === 'ad'}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>Use country codes (e.g., US, IN, GB)</small>
                  </div>
                </div>

                {/* Image for Ad (if not already uploaded) */}
                {!formData.image && (
                  <div className="form-group">
                    <label>Ad Image (Recommended)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'image')}
                      disabled={uploading}
                    />
                    {uploading && <p className="upload-status">Uploading...</p>}
                  </div>
                )}
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {formData.scheduledDate ? 'Schedule Post' : 'Post Now'}
              </button>
              <button
                type="button"
                onClick={() => setFormData({
                  platform: activeTab,
                  type: 'post',
                  content: '',
                  image: '',
                  video: '',
                  scheduledDate: '',
                  scheduledTime: '',
                  targetAudience: '',
                  budget: '',
                  dailyBudget: '',
                  duration: '',
                  status: 'pending',
                  adAccountId: '',
                  campaignName: '',
                  adSetName: '',
                  adName: '',
                  objective: 'OUTCOME_TRAFFIC',
                  callToActionType: 'LEARN_MORE',
                  link: '',
                  ageMin: 18,
                  ageMax: 65,
                  gender: 'all',
                  countries: 'US'
                })}
                className="btn btn-secondary"
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Scheduled Posts List */}
        <div className="scheduled-posts-section">
          <h3>Scheduled & Published Posts</h3>
          {loading ? (
            <p>Loading...</p>
          ) : scheduledPosts.length === 0 ? (
            <p className="no-posts">No scheduled posts found for {platforms.find(p => p.id === activeTab)?.name}</p>
          ) : (
            <div className="scheduled-posts-list">
              {scheduledPosts.map((post) => (
                <div key={post._id} className="scheduled-post-card">
                  <div className="post-header">
                    <div className="post-type-badge">
                      {(() => {
                        const postType = postTypes.find(t => t.value === post.type);
                        const Icon = postType?.icon;
                        return Icon ? <Icon /> : null;
                      })()}
                      <span>{postTypes.find(t => t.value === post.type)?.label || post.type}</span>
                    </div>
                    <span className={`status-badge ${post.status}`}>{post.status}</span>
                  </div>
                  <div className="post-content">
                    {post.image && (
                      <img src={post.image} alt="Post" className="post-preview-image" />
                    )}
                    {post.video && (
                      <video src={post.video} controls className="post-preview-video" />
                    )}
                    <p className="post-text">{post.content}</p>
                  </div>
                  <div className="post-meta">
                    <div className="post-info">
                      <span><strong>Platform:</strong> {post.platform}</span>
                      {post.scheduledDateTime && (
                        <span>
                          <strong>Scheduled:</strong> {new Date(post.scheduledDateTime).toLocaleString()}
                        </span>
                      )}
                      {post.budget && (
                        <span><strong>Budget:</strong> ₹{post.budget}</span>
                      )}
                      {post.duration && (
                        <span><strong>Duration:</strong> {post.duration} days</span>
                      )}
                    </div>
                    <div className="post-actions">
                      <button
                        onClick={() => handleDelete(post._id)}
                        className="btn btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationManager;

